import type { DocumentCallback, DocumentThresholds } from "@innovatrics/dot-document-auto-capture";
import {
  dispatchControlEvent,
  DocumentCustomEvent,
  ControlEventInstruction,
} from "@innovatrics/dot-document-auto-capture/events";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "../../../styles/index.module.css";
import commonStyles from "../../../styles/common.module.css";
import DocumentCamera from "./DocumentCamera";
import DocumentUi from "./DocumentUi";
import Image from "../../common/Image";
import ProcessingDescription from "../common/ProcessingDescription";
import { useTranslation } from "react-i18next";
import { BACK_DOCUMENT_BRIGHTNESS_HIGH_THRESHOLD, BACK_DOCUMENT_BRIGHTNESS_LOW_THRESHOLD, BACK_DOCUMENT_CONFIDENCE_THRESHOLD, BACK_DOCUMENT_HOTSPOTS_SCORE_THRESHOLD, BACK_DOCUMENT_OUT_OF_BOUNDS_THRESHOLD, BACK_DOCUMENT_SHARPNESS_THRESHOLD, BACK_DOCUMENT_SIZE_SMALL_THRESHOLD, FRONT_DOCUMENT_BRIGHTNESS_HIGH_THRESHOLD, FRONT_DOCUMENT_BRIGHTNESS_LOW_THRESHOLD, FRONT_DOCUMENT_CONFIDENCE_THRESHOLD, FRONT_DOCUMENT_HOTSPOTS_SCORE_THRESHOLD, FRONT_DOCUMENT_OUT_OF_BOUNDS_THRESHOLD, FRONT_DOCUMENT_SHARPNESS_THRESHOLD, FRONT_DOCUMENT_SIZE_SMALL_THRESHOLD } from "../../../config";

interface Props {
  selectedDocumentType: string;
  onNext: () => void;
  onPhotoTaken: DocumentCallback;
  onError: (error: Error) => void;
  photoUrl: string | undefined;
  documentCaptureSide: string;
  setPhotoUrl: Dispatch<SetStateAction<string | undefined>>;
}

function DocumentAutoCapture({
  selectedDocumentType,
  onNext,
  onPhotoTaken,
  onError,
  photoUrl,
  documentCaptureSide,
  setPhotoUrl,
}: Props) {
  const [processing,setProcessing] = useState(false);
  const handlePhotoTaken: DocumentCallback = async (imageData, content) => {
      setProcessing(true)
      setAppStateInstructions({
        ...appStateInstructions,
        waiting: {
          ...appStateInstructions.waiting,
          text: t('processing'),
          visible: true,
        },
      });
    onPhotoTaken(imageData, content);
  };
  const {t} = useTranslation()
  const documentUIInstructions = { 
    document_too_far:t('document.capture.movecloser'),
    sharpness_too_low:t('document.capture.movetobrighter'),
    brightness_too_low:t('document.capture.movetobrighter'),
    candidate_selection:t('document.capture.holdstill'),
    document_centering:t('document.capture.placeinrectangle'),
    document_not_present:t('document.capture.placeinrectangle'),
    brightness_too_high:t('document.capture.lesslight'),
    hotspots_present:t('document.capture.avoidreflections'), 
  }

  const frontDocumentCameraThresholds: DocumentThresholds = {
    confidenceThreshold:FRONT_DOCUMENT_CONFIDENCE_THRESHOLD,
    brightnessHighThreshold:FRONT_DOCUMENT_BRIGHTNESS_HIGH_THRESHOLD,
    brightnessLowThreshold:FRONT_DOCUMENT_BRIGHTNESS_LOW_THRESHOLD,
    outOfBoundsThreshold:FRONT_DOCUMENT_OUT_OF_BOUNDS_THRESHOLD,
    sharpnessThreshold:FRONT_DOCUMENT_SHARPNESS_THRESHOLD,
    hotspotsScoreThreshold:FRONT_DOCUMENT_HOTSPOTS_SCORE_THRESHOLD,
    sizeSmallThreshold:FRONT_DOCUMENT_SIZE_SMALL_THRESHOLD,
  }

  const backDocumentCameraThresholds: DocumentThresholds = {
    confidenceThreshold:BACK_DOCUMENT_CONFIDENCE_THRESHOLD,
    brightnessHighThreshold:BACK_DOCUMENT_BRIGHTNESS_HIGH_THRESHOLD,
    brightnessLowThreshold:BACK_DOCUMENT_BRIGHTNESS_LOW_THRESHOLD,
    outOfBoundsThreshold:BACK_DOCUMENT_OUT_OF_BOUNDS_THRESHOLD,
    sharpnessThreshold:BACK_DOCUMENT_SHARPNESS_THRESHOLD,
    hotspotsScoreThreshold:BACK_DOCUMENT_HOTSPOTS_SCORE_THRESHOLD,
    sizeSmallThreshold:BACK_DOCUMENT_SIZE_SMALL_THRESHOLD,
  }

  const getDocumentSideThresholds = (side:string):DocumentThresholds => {
    switch (side) {
      case "FRONT_SIDE":
        return frontDocumentCameraThresholds;
      case "BACK_SIDE":
        return backDocumentCameraThresholds;
      default:
        return backDocumentCameraThresholds;
    }
  };


  const getDocumentSideTitle = (title: string): string => {
    switch (title) {
      case "FRONT_SIDE":
        return t('primarydocument.capture.front');
      case "BACK_SIDE":
        return t('primarydocument.capture.back');
      default:
        return "Unknown";
    }
  };


  const [appStateInstructions, setAppStateInstructions] = useState({
    loading: { text: `${t('document.capture.loading')}`, visible: true },
    waiting: {
      text: "Rotate the document",
      visible: false,
    },
  });

  const sessionToken = sessionStorage.getItem("sessionToken") as string;

  return (
    <>
      <h1 className={commonStyles.header}>{t('primarydocument.title')}</h1>
      <p className={commonStyles.description}>
        {!photoUrl && t('primarydocument.capture.description')} <strong>{getDocumentSideTitle(documentCaptureSide)}</strong>{" "}
        {t('primarydocument.capture.ofyour')} {t(selectedDocumentType)}
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection:'column',
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        {/* parent container must have position: relative */}
        <div className={styles.container}>
          <DocumentCamera
            sessionToken={sessionToken}
            cameraFacing="environment"
            onPhotoTaken={handlePhotoTaken}
            onError={onError}
            thresholds={getDocumentSideThresholds(documentCaptureSide)}
          />
          <DocumentUi
            showCameraButtons
            appStateInstructions={appStateInstructions}
            instructions={documentUIInstructions}
          />
          <div className={commonStyles.popup}>
            {photoUrl && (
              <Image src={photoUrl} className={commonStyles.popupImg}></Image>    
            )}
            </div>
        </div>
        {processing && photoUrl && <ProcessingDescription content={t('primarydocument.capture.processing.description')} />}
      </div>
    </>
  );
}

export default DocumentAutoCapture;
